import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { TAB_MORE_COPY } from "@fomofi/shared";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MORE_ITEMS, PHONE_TABS, lit, moreIsLit } from "@/lib/phoneTabs";
import { cn } from "@/lib/utils";

/**
 * Phone-width bottom tabs (Anthony's Transactions redesign, Phase 3; decision note in
 * report -102, approved by Paul 2026-10-08). Below 768px only: tablets keep the header
 * and drawer, desktop keeps the sidebar, both unchanged.
 *
 * - Five tabs, Anthony's words: Home, Budget, Transactions, Forecast, More.
 * - More is a bottom sheet listing every other destination, so every page reachable
 *   before is reachable now. More is lit on any page it lists.
 * - Sheets, side panels and dialogs are z-50 overlays; the bar is z-40, so a panel's
 *   Save is never under it.
 * - The bar hides while a text field has focus, so with the keyboard open it can never
 *   cover the field being typed in.
 * - It clears the iPhone home indicator with env(safe-area-inset-bottom) (viewport-fit
 *   is cover), and `--tabbar-space` moves the feedback button and Back to top above it.
 */
/** True while a text field has focus: the on-screen keyboard is up. */
function useTypingFocus() {
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const isTextField = (el: EventTarget | null) =>
      el instanceof HTMLElement &&
      (el.isContentEditable ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement ||
        (el instanceof HTMLInputElement &&
          !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes(
            el.type,
          )));
    const onIn = (e: FocusEvent) => setTyping(isTextField(e.target));
    const onOut = () => setTyping(isTextField(document.activeElement));
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);
  return typing;
}

const itemClass = (active: boolean) =>
  cn(
    "flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-[11px] font-medium leading-tight transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    active ? "text-primary" : "text-muted-foreground hover:text-foreground",
  );

/**
 * A label, shown for every tab when five fit across, and for the ACTIVE tab only when
 * they don't (a large text size, or 200% zoom: "Transactions" can't fit a fifth of
 * 390px at 2x; rule 15, decision in report -98 Q6). Every tab keeps its accessible name
 * either way. The breakpoint is in rem, so it follows the user's text size.
 */
function TabLabel({ label, active }: { label: string; active: boolean }) {
  return (
    <span className={cn("max-w-full truncate", !active && "hidden @[22rem]:block")}>{label}</span>
  );
}

export default function PhoneTabBar() {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const typing = useTypingFocus();
  const moreLit = moreIsLit(pathname);

  return (
    <>
      <nav
        aria-label="Main"
        data-phone-tabbar=""
        className={cn(
          "@container fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur-xl md:hidden",
          "pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]",
          typing && "hidden",
        )}
      >
        {/* Horizontal padding is trimmed so "Transactions" fits its fifth (Paul's iPhone 17 Pro
            check, 2026-10-09): at 11px medium Plus Jakarta Sans it measures 66.34px, and the
            old px-2 / gap-1 / item px-1 left 66.0px at 402pt and 63.6px at 390pt, so it read
            "Transactio…". px-1 / no gap / item px-0.5 leaves 74.8px and 72.4px. */}
        <ul className="flex h-[var(--tabbar-h)] items-stretch px-1" role="list">
          {PHONE_TABS.map((tab) => {
            const active = lit(pathname, tab.lights);
            return (
              <li key={tab.to} className="flex min-w-0 flex-1" role="listitem">
                <NavLink
                  to={tab.to}
                  aria-label={tab.label}
                  aria-current={active ? "page" : undefined}
                  className={itemClass(active)}
                >
                  <tab.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  <TabLabel label={tab.label} active={active} />
                </NavLink>
              </li>
            );
          })}
          <li className="flex min-w-0 flex-1" role="listitem">
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-label={TAB_MORE_COPY}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              aria-current={moreLit ? "page" : undefined}
              className={itemClass(moreLit)}
            >
              <MoreHorizontal className="h-5 w-5 shrink-0" aria-hidden="true" />
              <TabLabel label={TAB_MORE_COPY} active={moreLit} />
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] gap-0 overflow-y-auto rounded-t-2xl p-0 pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
          aria-describedby={undefined}
        >
          <SheetHeader className="border-b border-border/60 px-5 py-4">
            <SheetTitle className="text-base">{TAB_MORE_COPY}</SheetTitle>
          </SheetHeader>
          <ul className="grid grid-cols-1 gap-1 p-3" role="list">
            {MORE_ITEMS.map((item) => {
              const active = lit(pathname, [item.to]);
              return (
                <li key={item.to} role="listitem">
                  <NavLink
                    to={item.to}
                    onClick={() => setMoreOpen(false)}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-foreground transition-colors hover:bg-accent",
                      active && "bg-accent",
                    )}
                  >
                    <item.icon
                      className="h-5 w-5 shrink-0 text-muted-foreground"
                      aria-hidden="true"
                    />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </SheetContent>
      </Sheet>
    </>
  );
}
